import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { StatsSection } from 'widgets/statspro/js/components/common/stats-section';

import {
    RoundListNavItem,
    CompNavItem,
    ResponsiveSeasonNavItem
} from 'common/react/components/competition-nav/nav-items';

import {
    TeamFilterItem,
    VenueFilterItem,
    CityFilterItem
} from 'common/react/components/competition-nav/filter-items';
import { FilterButton } from 'common/react/components/FilterButton';

import { navSelector } from 'store/modules/competition-nav/selectors/nav-selector';
import { useCompetitionNavigationOptions } from 'common/react/hooks/use-competition-navigation-options';
import { useNavSync } from 'common/react/components/competition-nav/hooks/use-nav-sync';
import { useWindowSizeV2 } from 'common/react/hooks/use-window-resize';
import { useGetCurrentCompFilters } from 'common/react/components/competition-nav/hooks/use-get-current-comp-filters';
import { SettingsButton } from 'common/react/components/SettingsButton';
import { useFiltersTracking } from 'common/react/hooks/use-filters-tracking';
import { FilterDropdown } from 'widgets/filters/js/components/FilterDropdown';

const MAX_SELECTIONS = 5;
const COUNTER_THRESHOLD = 2;

export const FixturesNav = ({
    competitionId,
    seasonId,
    competitionProviderId,
    seasonPid,
    onRoundClick,
    title,
    maximumSelections = MAX_SELECTIONS,
    counterThreshold = COUNTER_THRESHOLD
}) => {
    const windowSize = useWindowSizeV2();
    const [areFilterOptionsVisible, setAreFilterOptionsVisible] =
        useState(false);
    const [resetFilterBtnStyles, setResetFilterBtnStyles] = useState(false);

    const isResponsive = () =>
        windowSize.width <= PULSE.app.measurements.desktop;
    const [showResponsiveItems, setShowResponsiveItems] = useState(
        isResponsive()
    );

    useEffect(() => {
        setShowResponsiveItems(isResponsive());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [windowSize.width]);

    const initialValues = {
        competitionId,
        seasonId,
        competitionProviderId,
        seasonPid
    };

    const nav = useSelector(navSelector);
    const options = useCompetitionNavigationOptions(nav);

    useNavSync({ ...options, initialValues });

    const competitions = options.competitions;

    const seasons = useSelector(
        (state) =>
            state.seasons[`competition_${nav.competition.id}`]?.list ?? []
    );

    const currentFilters = useGetCurrentCompFilters();
    const competitionNavRef = useRef();

    const hasSeasonChanged = useCallback(() => {
        return nav.season.id !== nav.season.initialItemId;
    }, [nav.season.id, nav.season.initialItemId]);

    /**
     * custom hook providing tracking of filters
     */
    useFiltersTracking({
        title: `${title} filters`,
        filtersState: nav,
        options,
        filtersFields: [
            'competition',
            'season',
            'round',
            'teams',
            'venues',
            'cities'
        ],
        filtersRef: competitionNavRef
    });

    const renderMobile = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    title={title}
                    hideLabel={true}
                />
                <div className="stats-leaders__buttons-wrapper">
                    <FilterButton
                        classes="fixtures-nav__filter-button"
                        onChange={() =>
                            setAreFilterOptionsVisible((prev) => !prev)
                        }
                        areFilterOptionsVisible={areFilterOptionsVisible}
                        resetFilterBtnStyles={resetFilterBtnStyles}
                        setResetFilterBtnStyles={setResetFilterBtnStyles}
                        showFilterLabel={false}
                        showActiveCircle
                        isFilterSelectedCustom={hasSeasonChanged}
                    />
                    <StatsSection.SettingsButton classes="fixtures-nav__settings-button">
                        <SettingsButton />
                    </StatsSection.SettingsButton>
                </div>
            </StatsSection.NavItems>
            {currentFilters.competitionId &&
            currentFilters.compSeason?.id &&
            areFilterOptionsVisible ? (
                <StatsSection.NavFilterItems
                    title={title}
                    filterKeys={['teams', 'venues', 'cities']}
                >
                    <ResponsiveSeasonNavItem
                        type="with-default"
                        seasons={seasons}
                        title={title}
                    />
                    <TeamFilterItem
                        currentFilters={currentFilters}
                        title={title}
                        maximumSelections={parseInt(maximumSelections)}
                        counterThreshold={parseInt(counterThreshold)}
                    />
                    <VenueFilterItem
                        currentFilters={currentFilters}
                        title={title}
                        maximumSelections={parseInt(maximumSelections)}
                        counterThreshold={parseInt(counterThreshold)}
                    />
                    <CityFilterItem
                        currentFilters={currentFilters}
                        title={title}
                        maximumSelections={parseInt(maximumSelections)}
                        counterThreshold={parseInt(counterThreshold)}
                    />
                </StatsSection.NavFilterItems>
            ) : null}
            <StatsSection.RoundButtons>
                <RoundListNavItem
                    classes={'competition-nav__round-filter-wrapper'}
                    onRoundClick={onRoundClick}
                    title={title}
                />
            </StatsSection.RoundButtons>
        </>
    );

    const renderDesktop = () => (
        <>
            <StatsSection.NavItems>
                <CompNavItem
                    classes="fixtures-nav__comp-select"
                    competitions={competitions}
                    title={title}
                    hideLabel={false}
                />
                <FilterDropdown
                    title={title}
                    icon="season-trophy"
                    navItem="season"
                    items={seasons}
                />
            </StatsSection.NavItems>
            {currentFilters.competitionId && currentFilters.compSeason?.id ? (
                <StatsSection.NavFilterItems
                    title={title}
                    filterKeys={['teams', 'venues', 'cities']}
                >
                    <TeamFilterItem
                        currentFilters={currentFilters}
                        title={title}
                        maximumSelections={parseInt(maximumSelections)}
                        counterThreshold={parseInt(counterThreshold)}
                    />
                    <VenueFilterItem
                        currentFilters={currentFilters}
                        title={title}
                        maximumSelections={parseInt(maximumSelections)}
                        counterThreshold={parseInt(counterThreshold)}
                    />
                    <CityFilterItem
                        currentFilters={currentFilters}
                        title={title}
                        maximumSelections={parseInt(maximumSelections)}
                        counterThreshold={parseInt(counterThreshold)}
                    />
                </StatsSection.NavFilterItems>
            ) : null}
            <StatsSection.SettingsButton>
                <SettingsButton />
            </StatsSection.SettingsButton>
            <StatsSection.RoundButtons>
                <RoundListNavItem
                    classes={'competition-nav__round-filter-wrapper'}
                    onRoundClick={onRoundClick}
                    title={title}
                />
            </StatsSection.RoundButtons>
        </>
    );

    return (
        <div
            className="competition-nav filter-dropdown"
            ref={competitionNavRef}
        >
            <StatsSection.Main>
                <StatsSection.Nav
                    showResponsiveItems={showResponsiveItems}
                    withScrollableFilter={!showResponsiveItems}
                >
                    {showResponsiveItems ? renderMobile() : renderDesktop()}
                </StatsSection.Nav>
            </StatsSection.Main>
        </div>
    );
};

FixturesNav.propTypes = {
    competitionId: PropTypes.string,
    competitionProviderId: PropTypes.string,
    seasonPid: PropTypes.string,
    seasonId: PropTypes.string,
    onRoundClick: PropTypes.func,
    title: PropTypes.string,
    maximumSelections: PropTypes.string,
    counterThreshold: PropTypes.string
};
